import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import Clickable from 'components/Common/Clickable/Clickable'
import Divider from 'LuxKit/Divider'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import LineCardAtmIcon from 'LuxKit/Icons/line/LineCardAtmIcon'
import LineDashboardIcon from 'LuxKit/Icons/line/LineDashboardIcon'
import LinePumpIcon from 'LuxKit/Icons/line/LinePumpIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import type { SvgIconProps } from 'LuxKit/SvgIcon'
import { rem } from 'polished'
import type { ComponentType, ReactNode } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import CarHireImportantDetailsContent from './CarHireImportantDetailsContent'
import LoadingSpinner from 'components/Common/Loading/LoadingSpinner'
import { max, unique } from 'lib/array/arrayUtils'
import useCarHireOfferImportantInformation from 'hooks/CarHire/useCarHireImportantInformation'
import useCarHireImportantInformationCategoryLabels from 'hooks/CarHire/useCarHireImportantInformationCategoryLabels'
import { useAppSelector } from 'hooks/reduxHooks'
import { formatRoundedAmount } from 'lib/format/formatCurrencyIntl'
import carHireGlossaryMessages from 'i18n/carHireGlossaryMessages'
import List from 'components/Common/List'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useCarHireFuelPolicyLabel from 'hooks/CarHire/useCarHireFuelPolicyLabel'
import CarHireImportantInformationText, { normaliseCarHireImportantInformationParagraph } from './CarHireImportantInformationText'
import Expander from 'components/utils/Expander'
import { TabOptions } from './CarHireInclusionsAndDetailsModal.types'

export { TabOptions } from './CarHireInclusionsAndDetailsModal.types'

const ModalContent = styled(VerticalSpacer)`
  min-height: ${rem(360)};
`

const LoadingWrapper = styled(Group)`
  min-height: ${rem(275)};
`

const SectionCard = styled(VerticalSpacer)`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${rem(8)};
  overflow: hidden;
  padding: ${rem(20)} 0;
`

const SectionCardHeader = styled.div`
  padding: 0 ${rem(20)} ${rem(20)};
`

const SectionCardContent = styled.div`
  padding: ${rem(20)};
`

const SummaryRows = styled.div`
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${rem(16)};
  align-items: start;
  padding: ${rem(20)};

  &:not(:last-child) {
    border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  ${mediaQueryUp.tablet} {
    grid-template-columns: minmax(${rem(220)}, 30%) minmax(0, 1fr);
    gap: ${rem(24)};
  }
`

const SummaryHeading = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: ${rem(12)};
  align-items: start;
`

const SummaryIcon = styled.div`
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  padding-top: ${rem(2)};
`

const SummaryText = styled(VerticalSpacer)`
  min-width: 0;
`

const SummaryDescription = styled(VerticalSpacer)`
  min-width: 0;
`

const TermsCard = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${rem(8)};
  overflow: hidden;
`

const TermsHeader = styled(VerticalSpacer)`
  padding: ${rem(20)} ${rem(20)} ${rem(16)};
`

const TermsAccordionTrigger = styled(Clickable)`
  width: 100%;
  min-height: auto;
  padding: ${rem(16)} ${rem(20)} ${rem(8)};
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${rem(16)};
  align-items: center;

  &:focus-visible {
    outline: 2px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    outline-offset: -2px;
  }
`

const TermsAccordionContent = styled.div`
  padding: 0 ${rem(20)} ${rem(12)};
`

const TermsAccordionChevron = styled(Group)`
  justify-self: end;
`

const InclusionsList = styled(List)`
  padding-top: ${rem(8)};

  ${mediaQueryUp.tablet} {
    &.columns {
      column-count: 2;
    }
  }
`

interface Props {
  offer: App.CarHireOffer
  pickUpDate: string
  dropOffDate: string
  pickUpTime: string
  dropOffTime: string
  showInclusions: boolean
  title: string
  openTab?: App.CarHireImportantInformationCategory
}

interface SummaryDetail {
  label: string
  value?: string
  description?: string
  Icon: ComponentType<SvgIconProps>
}

interface TermsAccordionProps {
  children: ReactNode
  defaultOpen: boolean
  heading: string
}

function TermsAccordion(props: TermsAccordionProps) {
  const { children, defaultOpen, heading } = props
  const [isOpen, setIsOpen] = useState(defaultOpen)

  const handleClick = () => {
    setIsOpen(open => !open)
  }

  return (
    <>
      <TermsAccordionTrigger onClick={handleClick}>
        <BodyText variant="large" weight="bold">{heading}</BodyText>
        <TermsAccordionChevron direction="horizontal" verticalAlign="center">
          <DropdownChevron size="M" direction={isOpen ? 'up' : 'down'} />
        </TermsAccordionChevron>
      </TermsAccordionTrigger>
      <Expander expanded={isOpen}>
        <TermsAccordionContent>
          {children}
        </TermsAccordionContent>
      </Expander>
      <Divider kind="primary" />
    </>
  )
}

function getMaxExcess(pricedCoverages: Array<App.CarHirePricedCoverage>) {
  return max(pricedCoverages.filter(coverage => coverage.deductibleValue !== undefined), coverage => coverage.deductibleValue ?? 0)?.deductibleValue
}

function getCategoryParagraph(offerImportantInformation: Array<App.CarHireImportantInformation> | undefined, category: App.CarHireImportantInformationCategory, titleIncludes?: string) {
  const matchingInformation = offerImportantInformation?.find((item) => {
    return item.category === category && (!titleIncludes || item.title?.toLowerCase().includes(titleIncludes.toLowerCase()))
  })

  const paragraph = matchingInformation?.paragraphs.find(Boolean)

  return paragraph ? normaliseCarHireImportantInformationParagraph(paragraph) : undefined
}

function CarHireInclusionsAndDetailsModal(props: Props) {
  const {
    offer,
    pickUpDate,
    dropOffDate,
    pickUpTime,
    dropOffTime,
    showInclusions,
    title,
    openTab,
  } = props
  const intl = useIntl()
  const tabLabels = useCarHireImportantInformationCategoryLabels()
  const getFuelPolicyLabel = useCarHireFuelPolicyLabel()
  const regionCode = useAppSelector((state: App.State) => state.geo.currentRegionCode)
  const modalBodyRef = useRef<HTMLDivElement | null>(null)
  const { inclusions, fuelPolicy } = offer.copy

  const rateOption = offer.rateOptions[0]

  useEffect(() => {
    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTop = 0
    }
  }, [rateOption.id])

  const params: App.CarHireOfferImportantInformationParams = {
    pickUpLocation: {
      code: offer.pickUp.id,
      date: pickUpDate || '',
      time: pickUpTime || '',
    },
    dropOffLocation: {
      code: offer.dropOff.id,
      date: dropOffDate || '',
      time: dropOffTime || '',
    },
    reference: {
      id: rateOption.id,
      idContext: rateOption.idContext,
      type: rateOption.type,
      dateTime: rateOption.dateTime,
      url: rateOption.url,
    },
  }

  const { offerImportantInformation } = useCarHireOfferImportantInformation(params, rateOption.id)
  const isFetching = offerImportantInformation.fetching
  const hasError = Boolean(offerImportantInformation.error)
  const offerImportantInformationData = offerImportantInformation.data

  const termsSections: Array<App.CarHireImportantInformationCategory> = useMemo(() => {
    const tabs = Array.from(new Set((offerImportantInformationData || [])
      .filter(item => item.paragraphs.length > 0)
      .map(item => item.category)))

    if (fuelPolicy) {
      tabs.unshift(TabOptions.DRIVER_AND_VEHICLE)
    }

    if (tabs.includes(TabOptions.COVERAGE)) {
      tabs.unshift(TabOptions.COVERAGE)
    }

    if (inclusions.length > 0 && showInclusions) {
      tabs.unshift(TabOptions.INCLUSIONS)
    }

    return unique(tabs)
  }, [fuelPolicy, inclusions, offerImportantInformationData, showInclusions])

  const depositLabel = intl.formatMessage(carHireGlossaryMessages.refundableSecurityDeposit)
  const excessLabel = intl.formatMessage(carHireGlossaryMessages.damageAndTheftExcess)
  const maxExcess = getMaxExcess(offer.copy.pricedCoverages)

  const unlimitedMileageLabel = intl.formatMessage({
    id: 'Il8htwBl8fAtf7R-uf75',
    defaultMessage: 'Unlimited mileage',
    description: 'Label indicating unlimited mileage in car hire important info summary',
  })

  const limitedMileageLabel = intl.formatMessage(
    {
      id: 'p2zkTkvNWcOTIWWiFjGS',
      defaultMessage: '{quantity}{unit}/{period} rental mileage limit',
      description: 'Label showing rental mileage limit in car hire important info summary, e.g. 300km/day rental mileage limit',
    },
    {
      quantity: offer.mileage.quantity,
      unit: offer.mileage.unit,
      period: offer.mileage.period,
    },
  )

  const mileageDescription = intl.formatMessage({
    id: 'eDjvT7WqDTWbZIuWm6NQ',
    defaultMessage: 'Your rental includes the mileage allowance shown here.',
    description: 'Description of mileage shown in car hire important info summary',
  })

  const mileageLabel = intl.formatMessage({
    id: 'PsPMMILaocUt2iNGaeBB',
    defaultMessage: 'Mileage',
    description: 'Label for mileage in car hire important info summary',
  })

  const fuelPolicyLabel = intl.formatMessage({
    id: 'tS7zxh1c11QPyFBaPLjN',
    defaultMessage: 'Fuel policy',
    description: 'Label for fuel policy in car hire important info summary',
  })

  const includedInPriceLabel = intl.formatMessage({
    id: '8Y7Y6YYLNhLkh5dTOwn0',
    defaultMessage: 'Included in the rental price',
    description: 'Label for included inclusions in car hire important info summary',
  })

  const includedInPriceDescription = intl.formatMessage({
    id: '6eNFaFkEdwo6kUOvTeoR',
    defaultMessage: 'Key inclusions are included below. Check the full rental terms for the complete policy wording.',
    description: 'Description for included inclusions in car hire important info summary',
  })

  const keyDetailsHeading = intl.formatMessage({
    id: 'pZqTSV78B-sH7ljVk5U_',
    defaultMessage: 'Important info',
    description: 'Heading for key details in car hire important details modal',
  })

  const rentalTermsHeading = intl.formatMessage({
    id: '1rXUExRrfwgs5KMLLaYx',
    defaultMessage: 'Full rental terms',
    description: 'Heading for full rental terms section in car hire important details modal',
  })

  const rentalTermsDescription = intl.formatMessage({
    id: 'l9hbPubNENemZXylHwa6',
    defaultMessage: 'Review the supplier terms by section before you book.',
    description: 'Description for full rental terms section in car hire important details modal',
  })

  const summaryDetails: Array<SummaryDetail> = [
    ...(offer.copy.depositInfo ? [{
      label: depositLabel,
      value: formatRoundedAmount(offer.copy.depositInfo.amount, offer.copy.depositInfo.currency, regionCode),
      description: getCategoryParagraph(offerImportantInformationData, TabOptions.DEPOSIT_AND_PAYMENT, 'deposit'),
      Icon: LineCardAtmIcon,
    }] : []),
    ...(maxExcess !== undefined ? [{
      label: excessLabel,
      value: formatRoundedAmount(maxExcess, offer.rateOptions[0]?.currency ?? '', regionCode),
      description: getCategoryParagraph(offerImportantInformationData, TabOptions.COVERAGE),
      Icon: LineShieldCheckIcon,
    }] : []),
    {
      label: mileageLabel,
      value: offer.mileage.unlimited ? unlimitedMileageLabel : limitedMileageLabel,
      description: mileageDescription,
      Icon: LineDashboardIcon,
    },
    ...(fuelPolicy ? [{
      label: fuelPolicyLabel,
      description: getCategoryParagraph(offerImportantInformationData, TabOptions.DRIVER_AND_VEHICLE, 'fuel') ?? getFuelPolicyLabel(fuelPolicy),
      Icon: LinePumpIcon,
    }] : []),
  ]

  return (
    <Modal ref={modalBodyRef} title={title} size="L">
      <ModalContent gap={24}>
        <SectionCard gap={0}>
          <SectionCardHeader>
            <Heading variant="heading4">{keyDetailsHeading}</Heading>
          </SectionCardHeader>
          <SummaryRows>
            {summaryDetails.map(({ label, value, description, Icon }) => (
              <SummaryRow key={label}>
                <SummaryHeading>
                  <SummaryIcon>
                    <Icon />
                  </SummaryIcon>
                  <SummaryText gap={4}>
                    <BodyText variant="medium" weight="bold">{label}</BodyText>
                    {value && <BodyText variant="medium">{value}</BodyText>}
                  </SummaryText>
                </SummaryHeading>
                {description && (
                  <SummaryDescription gap={4}>
                    <BodyText variant="medium">
                      <CarHireImportantInformationText text={description} />
                    </BodyText>
                  </SummaryDescription>
                )}
              </SummaryRow>
            ))}
          </SummaryRows>
          {showInclusions && inclusions.length > 0 && (
            <>
              <Divider kind="primary" />
              <SectionCardContent>
                <VerticalSpacer gap={4}>
                  <BodyText variant="medium" weight="bold">{includedInPriceLabel}</BodyText>
                  <BodyText variant="small" colour="neutral-one">{includedInPriceDescription}</BodyText>
                  <InclusionsList className={inclusions.length > 5 ? 'columns' : undefined}>
                    {inclusions.map(inclusion =>
                      <BodyText variant="medium" as="li" key={inclusion}>
                        {inclusion}
                      </BodyText>,
                    )}
                  </InclusionsList>
                </VerticalSpacer>
              </SectionCardContent>
            </>
          )}
        </SectionCard>

        <TermsCard>
          <TermsHeader gap={4}>
            <Heading variant="heading4">{rentalTermsHeading}</Heading>
            <BodyText variant="medium" colour="neutral-one">{rentalTermsDescription}</BodyText>
          </TermsHeader>
          <Divider kind="primary" />
          {isFetching && !hasError && termsSections.length === 0 &&
            <LoadingWrapper direction="horizontal" fullHeight horizontalAlign="center" verticalAlign="center">
              <LoadingSpinner size={48} />
            </LoadingWrapper>}

          {termsSections.map((section, index) => (
            <TermsAccordion
              key={section}
              heading={tabLabels[section] || section}
              defaultOpen={openTab ? section === openTab : index === 0}
            >
              <CarHireImportantDetailsContent
                offerImportantInformation={offerImportantInformationData}
                activeTab={section}
                offer={offer}
                hasError={hasError}
                isFetching={isFetching}
              />
            </TermsAccordion>
          ))}
        </TermsCard>
      </ModalContent>
    </Modal>
  )
}

export default CarHireInclusionsAndDetailsModal
